/**
 * anasayfaModulleri.js — firma portalı Ana Sayfa'sının hangi widget'ı ve hangi okumayı hangi portal modülüne bağladığı
 * (backlog #526, 2026-10-21). FirmaAnasayfaListPage.jsx kullanır; mantık sayfadan ayrı ki jest ile denenebilsin
 * (anasayfaModulleri.test.js).
 *
 * NEDEN: Yabancı Katılımcı setinde Standlar ve Talepler "Yok" yapılınca kenar menüden kalktılar ama Ana Sayfa'nın tamamı 403
 * gösterdi: sayfa altı okumayı tek Promise.all'da topluyordu, GET /standTalep/list ise STANDLAR en az READ ister (backend sert 403
 * verir, boş sayfa yok) ve tek reddedilen çağrı profili de düşürüyordu. Şimdi: setin ulaşamadığı modülün okuması hiç yapılmaz ve
 * widget'ı çizilmez (kenar menüyle aynı kural, bkz. VerticalLayouts PORTAL_MENU_MODULU), profil dışındaki okumalar sayfayı
 * düşürmez.
 */
import { enAz, MODUL, SEVIYE } from "../../services/portalService";

/**
 * Widget -> verisinin geldiği modüller (hepsi en az READ olmalı). Standlarım / Stand Durumu / Projeler / Talepler stand listesinden
 * türer (STANDLAR); Talepler ayrıca Talepler sayfasına gider (TALEPLER); Personeller Personel sayfasına gider (PERSONEL).
 * Projeler widget'ına PROJELER bilerek eklenmedi: davranışı değişmesin, o bir Portal-RBAC meselesi (#431 notu).
 */
export const WIDGET_MODULLERI = {
  standDurumu: [MODUL.STANDLAR],
  yuklemeler: [],
  personeller: [MODUL.PERSONEL],
  standlarim: [MODUL.STANDLAR],
  projeler: [MODUL.STANDLAR],
  talepler: [MODUL.STANDLAR, MODUL.TALEPLER],
  iletisim: [],
};

/** Yüklemeler widget'ındaki satırlar -> modülleri (satır kapalıysa çizilmez; "Firma Evrakları" satırı #432'den beri kendi kuralıyla). */
export const YUKLEME_SATIRI_MODULU = {
  personel: MODUL.PERSONEL,
  proje: MODUL.PROJELER,
  talep: MODUL.TALEPLER,
};

/**
 * Arayüz seti -> o sette Ana Sayfa'da AÇIK olan widget'lar. Yalnızca burada adı geçen setler kısıtlanır (öbürleri için kısıt yok);
 * modül kuralı (WIDGET_MODULLERI) bunun ÜSTÜNE uygulanır.
 * #527 (2026-10-21, Deniz): önemli uluslararası fuar için Yabancı Katılımcı yalnızca Yüklemeler ve İletişim görür, ötesi kapalı.
 * Geri almak için bu girdiyi silmek yeterli (WAS: kısıt yoktu, modül kuralı tek başına geçerliydi).
 * #704 (2026-11-03): bu dosyadaki her `setKodu` DÜZENİN kodudur (services/sadePanel.gorunumSetKodu): Sade panel modu AÇIKKEN her firma
 * girişi için YABANCI_KATILIMCI, yani bu tablolar o zaman herkese uygulanır. WAS (until #704): portal.aktif.kod.
 */
export const SET_WIDGETLARI = {
  YABANCI_KATILIMCI: ["yuklemeler", "iletisim"],
};

/**
 * Arayüz seti -> Yüklemeler widget'ında o sette GİZLENEN satırlar (YUKLEME_SATIRI_MODULU anahtarları).
 * #535 (2026-10-21, Deniz): Yabancı Katılımcı "Proje Yüklemeleri" satırını görmez. Geri almak için girdiyi silmek yeterli.
 */
export const SET_GIZLI_YUKLEME_SATIRLARI = {
  YABANCI_KATILIMCI: ["proje"],
};

/** Yüklemeler widget'ındaki satırların varsayılan sırası; `firma` = "Firma Evrakları" satırı (modül kuralı değil, #432'den beri kendi kuralı). */
export const YUKLEME_SATIRI_SIRASI = ["personel", "proje", "talep", "firma"];

/**
 * Arayüz seti -> Yüklemeler widget'ında o setin KENDİ satır sırası. Yalnızca burada adı geçen setlerin sırası değişir.
 * #607 (2026-10-30, Deniz): Yabancı Katılımcı'da "Firma Evrakları" ile "Personel Yüklemeleri" yer değiştirir (Firma Evrakları en üstte,
 * Personel Yüklemeleri en altta). Geri almak için girdiyi silmek yeterli (WAS: her set YUKLEME_SATIRI_SIRASI'nı kullanıyordu).
 */
export const SET_YUKLEME_SATIRI_SIRASI = {
  YABANCI_KATILIMCI: ["firma", "proje", "talep", "personel"],
};

/** Bu setin Yüklemeler satır sırası (setKodu = portal.aktif.kod; set yoksa / sırası tanımsızsa varsayılan). */
export const yuklemeSatiriSirasi = (setKodu = null) => SET_YUKLEME_SATIRI_SIRASI[setKodu] ?? YUKLEME_SATIRI_SIRASI;

/** `seviye`: portal.seviye gibi (modül) => NONE|READ|WRITE|MANAGE. */
export const modulOkunur = (seviye, modul) => enAz(seviye(modul), SEVIYE.READ);

/** Yüklemeler widget'ının bir satırı (`personel` | `proje` | `talep`) çizilir mi: setinde gizli değil ve modülü en az READ. */
export function yuklemeSatiriGorunur(satir, seviye, setKodu = null) {
  if ((SET_GIZLI_YUKLEME_SATIRLARI[setKodu] ?? []).includes(satir)) return false;
  return modulOkunur(seviye, YUKLEME_SATIRI_MODULU[satir]);
}

/**
 * Bilinmeyen widget anahtarı kapalı sayılmaz (modülsüz: iletişim, yüklemeler).
 * WAS (until #527): widgetGorunur(key, seviye) — yalnızca modül kuralı. `setKodu`: aktif setin kodu (portal.aktif.kod), yoksa null.
 */
export function widgetGorunur(key, seviye, setKodu = null) {
  const izinli = SET_WIDGETLARI[setKodu];
  if (izinli && !izinli.includes(key)) return false;
  return (WIDGET_MODULLERI[key] ?? []).every((modul) => modulOkunur(seviye, modul));
}

/** Stand listesinden beslenen widget'lar: hiçbiri çizilmeyecekse liste okunmaz. */
const STAND_WIDGETLARI = ["standDurumu", "standlarim", "projeler", "talepler"];

/**
 * Ana Sayfa'nın ayrı ayrı çektiği okumalar: GET /standTalep/list (STANDLAR; stand widget'larından biri çizilecekse),
 * GET /personnel/firma/{id} (PERSONEL — backend bu okumada modül sormaz ama widget'ı Personel sayfasına gider).
 * WAS (until #527): okumaPlani(seviye) -> { standlar: STANDLAR en az READ, personel: PERSONEL en az READ } (set kısıtı yoktu).
 */
export function okumaPlani(seviye, setKodu = null) {
  return {
    standlar: STAND_WIDGETLARI.some((k) => widgetGorunur(k, seviye, setKodu)),
    personel: widgetGorunur("personeller", seviye, setKodu),
  };
}
